// Geometry for the existing caller-owned HERE map; no map or request lifecycle.
import { hasPosition } from './historyData';

function ridePoints(ride) {
  const points = [];
  const push = (p) => {
    if (hasPosition(p)) {
      points.push({ lat: p.lat, lng: p.lng });
    }
  };
  push(ride.start);
  (ride.samples || []).forEach(push);
  push(ride.end);
  return points;
}

// A BOX FITTED EXACTLY PUTS THE ROUTE ON THE BORDER. HERE fits the rectangle it is given,
// so a day from Turku to Helsinki arrives with its departure dot on the left edge and its
// arrival dot off the right one -- the two marks that say which way the vehicle went.
// The camera gets a wider box than the route, and a standing vehicle gets a box at all.
const FIT_MARGIN = 0.12;
const FIT_MINIMUM_DEGREES = { lat: 0.002, lng: 0.004 };

function padBounds(box) {
  const latPad = Math.max(Math.abs(box.top - box.bottom) * FIT_MARGIN, FIT_MINIMUM_DEGREES.lat);
  const lngPad = Math.max(Math.abs(box.right - box.left) * FIT_MARGIN, FIT_MINIMUM_DEGREES.lng);
  return {
    top: Math.min(90, box.top + latPad),
    left: Math.max(-180, box.left - lngPad),
    bottom: Math.max(-90, box.bottom - latPad),
    right: Math.min(180, box.right + lngPad),
  };
}


// WHICH WAY THE VEHICLE WENT, WRITTEN ALONG THE LINE ITSELF. A colour and two dots leave the
// direction to a legend, and a legend is a thing the operator has to have read; chevrons
// pointing down the route do not need one.
//
// PLANAR BEARING WITH A COS(LAT) CORRECTION, not a great circle. A degree of longitude at
// 60N covers half the ground a degree of latitude does, so the uncorrected angle of a
// due-east leg comes out near 63 degrees and every arrow on the map leans north. Over the
// length of a Finnish journey the great-circle refinement is far under a degree -- invisible
// at eighteen pixels -- and this stays a function a test can read.
function bearingDegrees(from, to) {
  const midLat = ((from.lat + to.lat) / 2) * (Math.PI / 180);
  const east = (to.lng - from.lng) * Math.cos(midLat);
  const north = to.lat - from.lat;
  return ((Math.atan2(east, north) * 180) / Math.PI + 360) % 360;
}

// A HANDFUL OF ARROWS SPREAD ALONG THE RIDE, NOT ONE PER SAMPLE. The sampling interval is
// a clock, not a distance: ten minutes of motorway is fifteen kilometres and ten minutes of
// city is one, so an arrow per segment would be a dotted caterpillar in town and a bare line
// on the trunk road. Segments are picked by position so the arrows land evenly on the shape.
function arrowAnchors(points, wanted) {
  const segments = points.length - 1;
  if (segments < 1 || wanted < 1) {
    return [];
  }
  const count = Math.min(wanted, segments);
  const anchors = [];
  for (let i = 0; i < count; i += 1) {
    const seg = Math.min(segments - 1, Math.floor(((i + 0.5) * segments) / count));
    const from = points[seg];
    const to = points[seg + 1];
    anchors.push({
      lat: (from.lat + to.lat) / 2,
      lng: (from.lng + to.lng) / 2,
      bearing: bearingDegrees(from, to),
    });
  }
  return anchors;
}

// A phone shows the same route in a third of the width, where six arrows overlap into a
// smear and stop reading as arrows at all.
function arrowCountFor(screenSize) {
  return screenSize === 'small' ? 3 : 6;
}

// WHITE, ON EVERY ICON, BECAUSE THE TILE UNDERNEATH IS NOT OURS. The old markers filled the
// hollow end with `#1b1b1b` -- a dark-map assumption -- and on a light or satellite tile that
// dot read as filled, which is to say as the arrival. A white body or halo separates the
// mark from whatever it lands on in both directions.
const MARKER_HALO = '#FFFFFF';
const MARKER_BOX = { w: 18, h: 18 };

function markerSvg(body) {
  return '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">'
    + body + '</svg>';
}

// START AND END DIFFER IN SHAPE, not only in fill. Two circles telling each other apart by an
// attribute is exactly the legend this change is removing: a ring is where it set off, a
// square block is where it stopped.
function startMarkerSvg(colour) {
  return markerSvg('<circle cx="9" cy="9" r="5.5" fill="' + MARKER_HALO
    + '" stroke="' + colour + '" stroke-width="3"/>');
}

function endMarkerSvg(colour) {
  return markerSvg('<rect x="3.5" y="3.5" width="11" height="11" rx="2" fill="' + colour
    + '" stroke="' + MARKER_HALO + '" stroke-width="2"/>');
}

// H.map.Icon TAKES NO ROTATION, so the rotation goes inside the markup. The path points north
// at zero degrees and SVG rotates clockwise, which is what a compass bearing already means.
function arrowMarkerSvg(colour, degrees) {
  const angle = Math.round(degrees * 10) / 10;
  return markerSvg('<g transform="rotate(' + angle + ' 9 9)">'
    + '<path d="M9 2.5 L14 14.5 L9 11.5 L4 14.5 Z" fill="' + colour
    + '" stroke="' + MARKER_HALO + '" stroke-width="1.5" stroke-linejoin="round"/></g>');
}

export { ridePoints, padBounds, bearingDegrees, arrowAnchors, arrowCountFor,
  MARKER_BOX, startMarkerSvg, endMarkerSvg, arrowMarkerSvg };
